/**
 * Canonical reference type for any entity (attributes, sets, persons, etc.).
 * Use EntityRef when passing entity/attribute references; use isA or entityRefEquals for
 * comparison; use ref.id for decision/API logic and ref.displayName for labels.
 * Optional entityType identifies the kind of entity (e.g. "Set", "Sponsored").
 */

export interface EntityRef {
    id: string;
    displayName: string;
    /** Optional entity type (e.g. schema entity id or API EntityType). */
    entityType?: string;
}

const ENTITY_REF_ID_REQUIRED = 'EntityRef requires a non-empty id (do not fall back to displayName).';

/** Normalize a string for case-insensitive comparison (trim, optional $. prefix, collapse spaces and hyphens so "Filter Scope" and "FilterScope" match). */
export function normalizeKey(key: string): string {
    const s = (key ?? '').toString().trim();
    const withoutPrefix = s.startsWith('$.') ? s.slice(2) : s;
    return withoutPrefix.toLowerCase().replace(/[\s-]+/g, '');
}

/** Object form: must have id/Id or displayName/DisplayName; accepts PascalCase (API shape) and normalizes to camelCase. */
type EntityRefLike = {
    id?: string | null;
    displayName?: string | null;
    entityType?: string | null;
    Id?: string | null;
    DisplayName?: string | null;
    EntityType?: string | null;
};

export function isEntityRefShape(value: unknown): value is EntityRef {
    return (
        typeof value === 'object' &&
        value !== null &&
        'id' in value &&
        'displayName' in value &&
        typeof (value as EntityRef).id === 'string' &&
        typeof (value as EntityRef).displayName === 'string'
    );
}

/**
 * Creates an EntityRef from a string, an existing EntityRef, or an object with id or displayName.
 * - Existing EntityRef: validated and returned as-is if valid; throws validation errors if not.
 * - String starting with "$.": treated as id; displayName is the rest after "$.".
 * - String not starting with "$.": treated as displayName; id is "$." + that string.
 * - Object with id: uses id; displayName defaults to id (without "$." if present). Optional entityType.
 * - Object with displayName (no id): id becomes "$." + displayName. Optional entityType.
 */
export function createEntityRef(value: string | EntityRef | EntityRefLike): EntityRef {
    if (value == null) {
        throw new Error(ENTITY_REF_ID_REQUIRED);
    }
    if (isEntityRefShape(value)) {
        const errors = entityRefValidate(value);
        if (errors.length > 0) {
            throw new Error(errors.join(' '));
        }
        return value;
    }
    if (typeof value === 'string') {
        const s = value.trim();
        if (!s) throw new Error(ENTITY_REF_ID_REQUIRED);
        if (s.startsWith('$.')) {
            const id = s;
            const displayName = s.slice(2).trim() || s;
            return { id, displayName };
        }
        return { id: `$.${s}`, displayName: s };
    }
    // Object: accept camelCase or PascalCase (Id/DisplayName from API); normalize to id/displayName for logic
    const rawId = (value.id ?? value.Id ?? '').toString().trim();
    const rawDisplayName = (value.displayName ?? value.DisplayName ?? '').toString().trim();
    const hasId = rawId !== '';
    const hasDisplayName = rawDisplayName !== '';
    if (!hasId && !hasDisplayName) {
        throw new Error(ENTITY_REF_ID_REQUIRED + ' Pass a string, an object with id/Id, or an object with displayName/DisplayName.');
    }
    const entityType = (value.EntityType ?? value.entityType) != null ? String(value.EntityType ?? value.entityType).trim() : undefined;
    if (hasId) {
        const id = rawId;
        const displayName = hasDisplayName ? rawDisplayName : (id.startsWith('$.') ? id.slice(2).trim() || id : id);
        return { id, displayName: displayName || id, ...(entityType && { entityType }) };
    }
    const displayName = rawDisplayName;
    return { id: `$.${displayName}`, displayName, ...(entityType && { entityType }) };
}

/**
 * Try to create an EntityRef from an unknown reference value (e.g. API response or form).
 * Returns null for null/undefined, invalid values, or when id/displayName cannot be determined.
 * - String: if it starts with "$." that is the id and the rest is displayName; otherwise the string is displayName and id is "$." + it.
 * - Object: uses createEntityRef (accepts Id/id, DisplayName/displayName); returns null if it throws.
 */
export function tryCreateEntityRef(value: unknown): EntityRef | null {
    if (value == null) return null;
    if (typeof value === 'string') {
        const s = value.trim();
        if (!s) return null;
        if (s.startsWith('$.')) {
            const id = s;
            const displayName = s.slice(2).trim() || s;
            return { id, displayName };
        }
        return { id: `$.${s}`, displayName: s };
    }
    if (typeof value !== 'object') return null;
    try {
        return createEntityRef(value as EntityRefLike);
    } catch {
        return null;
    }
}

/**
 * Get the id from a reference value (string or object with Id/id). Throws if value is null/undefined
 * or has no id/Id. Use for payload serialization where a valid reference is required.
 * @param mustBeAttribute - When true, throws if the resolved id does not start with "$.".
 */
export function getId(
    value: unknown,
    options?: { mustBeAttribute?: boolean }
): string {
    if (value == null) {
        throw new Error('getId: value is null or undefined.');
    }
    let id: string;
    if (typeof value === 'string') {
        id = value.trim();
        if (id === '') throw new Error('getId: string value is empty.');
    } else if (typeof value === 'object') {
        const raw = (value as Record<string, unknown>).Id ?? (value as Record<string, unknown>).id;
        if (raw == null) {
            throw new Error('getId: object has no Id or id.');
        }
        id = String(raw).trim();
        if (id === '') throw new Error('getId: object Id/id is empty.');
    } else {
        throw new Error('getId: value must be a string or object with Id/id.');
    }
    if (options?.mustBeAttribute && !id.startsWith('$.')) {
        throw new Error('getId: mustBeAttribute is true but id does not start with "$.".');
    }
    return id;
}

/**
 * Same as getId but returns null instead of throwing when value is invalid or fails mustBeAttribute.
 */
export function tryGetId(
    value: unknown,
    options?: { mustBeAttribute?: boolean }
): string | null {
    try {
        return getId(value, options);
    } catch {
        return null;
    }
}

export interface EntityRefValidateOptions {
    /** When true (e.g. for attribute refs), id must be prefixed with "$." and not whitespace after. Default false. */
    requireIdPrefix?: boolean;
}

/**
 * Validates an EntityRef and returns an array of error messages (empty if valid).
 * - displayName must not be null or empty.
 * - id must not be null or whitespace.
 * - If options.requireIdPrefix: id must be prefixed with "$." and not be null or only whitespace after the "$.".
 */
export function entityRefValidate(
    ref: EntityRef | { id?: string | null; displayName?: string | null },
    options?: EntityRefValidateOptions
): string[] {
    const errors: string[] = [];
    const displayName = ref?.displayName;
    if (displayName == null || String(displayName).trim() === '') {
        errors.push('displayName must not be null or empty.');
    }
    const id = ref?.id;
    if (id == null) {
        errors.push(options?.requireIdPrefix
            ? 'id must be prefixed with "$." and not be null or whitespace after the "$.".'
            : 'id must not be null or empty.');
        return errors;
    }
    const idStr = String(id);
    if (options?.requireIdPrefix) {
        if (!idStr.startsWith('$.')) {
            errors.push('id must be prefixed with "$.".');
        } else if (idStr.slice(2).trim() === '') {
            errors.push('id must not be null or whitespace after the "$.".');
        }
    } else if (idStr.trim() === '') {
        errors.push('id must not be null or empty.');
    }
    return errors;
}

/**
 * Returns true if this ref matches the given key or another EntityRef.
 * - For string: case-insensitive comparison against both id and displayName (handles $. prefix).
 * - For EntityRef: case-insensitive comparison of this ref's id vs the other's id.
 */
export function isA(ref: EntityRef, keyOrRef: string | EntityRef): boolean {
    if (!ref?.id) return false;
    if (typeof keyOrRef === 'string') {
        const k = normalizeKey(keyOrRef);
        if (!k) return false;
        const idNorm = normalizeKey(ref.id);
        const displayNorm = normalizeKey(ref.displayName);
        return idNorm === k || displayNorm === k;
    }
    if (keyOrRef?.id == null) return false;
    return normalizeKey(ref.id) === normalizeKey(keyOrRef.id);
}

/**
 * Returns true if the given static string refers to this ref. Case-insensitive comparison against both id and displayName.
 */
export function entityRefEquals(ref: EntityRef, key: string): boolean {
    if (!ref || !key) return false;
    const k = normalizeKey(key);
    if (!k) return false;
    const idNorm = normalizeKey(ref.id);
    const displayNorm = normalizeKey(ref.displayName);
    return idNorm === k || displayNorm === k;
}

/** Find a ref in a list that matches the given key (case-insensitive match on id or displayName). */
export function findEntityRefByKey(refs: EntityRef[], key: string): EntityRef | undefined {
    if (!key || !Array.isArray(refs)) return undefined;
    return refs.find((r) => entityRefEquals(r, key));
}

/** Get the form value for a ref from formData (key may be id, displayName, or $.prefixed). */
export function getFormValueByEntityRef(
    formData: Record<string, unknown>,
    ref: EntityRef
): unknown {
    if (!formData || typeof formData !== 'object' || !ref) return undefined;
    const key = Object.keys(formData).find((k) => entityRefEquals(ref, k));
    return key === undefined ? undefined : formData[key];
}

/** Check if formData has a truthy value for the given ref. */
export function hasFormValueForEntityRef(
    formData: Record<string, unknown>,
    ref: EntityRef
): boolean {
    if (ref.id === ref.displayName) { console.error('hasFormValueForEntityRef: ref.id === ref.displayName', ref.id, ref.displayName); }
    if (ref.displayName.startsWith('$.')) { console.error('hasFormValueForEntityRef: key starts with $.', ref.id); }
    const value = getFormValueByEntityRef(formData, ref);
    return value !== undefined && value !== null && value !== '';
}

/**
 * Get the entity value for a ref. Prefers the entity key that matches ref.displayName, then falls back to ref.id.
 */
export function getEntityValueByEntityRef(entity: Record<string, unknown> | null | undefined, ref: EntityRef): unknown {
    if (!entity || typeof entity !== 'object' || !ref?.id) return undefined;
    const keys = Object.keys(entity);
    const byDisplayName = keys.find((k) => normalizeKey(k) === normalizeKey(ref.displayName));
    if (byDisplayName !== undefined) return entity[byDisplayName];
    const byId = keys.find((k) => normalizeKey(k) === normalizeKey(ref.id));
    return byId !== undefined ? entity[byId] : undefined;
}

/** Whether the entity has a key that matches this ref (id or displayName). */
export function entityHasEntityRef(entity: Record<string, unknown> | null | undefined, ref: EntityRef): boolean {
    if (!entity || typeof entity !== 'object' || !ref?.id) return false;
    if (ref.id === ref.displayName) { console.error('entityHasEntityRef: ref.id === ref.displayName', ref.id, ref.displayName); }
    if (ref.displayName.startsWith('$.')) { console.error('entityHasEntityRef: key starts with $.', ref.id); }
    return Object.keys(entity).some((k) => entityRefEquals(ref, k));
}

/**
 * Set the entity value for a ref. Uses the same key resolution as getEntityValueByEntityRef:
 * prefers the existing key that matches ref.displayName, then ref.id. If no key exists, sets
 * entity[ref.displayName] so API payloads use DisplayName-style keys.
 */
export function setEntityValueByEntityRef(
    entity: Record<string, unknown>,
    ref: EntityRef,
    value: unknown
): void {
    if (!entity || typeof entity !== 'object' || !ref?.id) return;
    if (ref.id === ref.displayName) { console.error('setEntityValueByEntityRef: ref.id === ref.displayName', ref.id, ref.displayName); }
    if (ref.displayName.startsWith('$.')) { console.error('setEntityValueByEntityRef: key starts with $.', ref.id); }
    (entity as Record<string, unknown>)[ref.displayName] = value;
}

/**
 * Get value by attribute ref from an entity or entity-like record (e.g. form data).
 * Accepts string (e.g. "$.Description" or "Description") or EntityRef; uses the same key resolution
 * as getEntityValueByEntityRef (displayName first, then id) so it works for both API entities and
 * form data that loosely follows entity shape.
 */
export function getEntityValueFromARef(
    attrRef: string | EntityRef,
    entity: Record<string, unknown> | null | undefined
): unknown {
    const ref = typeof attrRef === 'string' ? createEntityRef(attrRef) : attrRef;
    return getEntityValueByEntityRef(entity, ref);
}

/**
 * Set entity value by attribute ref. Accepts string or EntityRef; uses the same key resolution
 * as getEntityValueByEntityRef (DisplayName first, then id) so entity shape stays consistent.
 */
export function setEntityValueFromARef(
    attrRef: string | EntityRef,
    entity: Record<string, unknown>,
    value: unknown
): void {
    const ref = typeof attrRef === 'string' ? createEntityRef(attrRef) : attrRef;
    setEntityValueByEntityRef(entity, ref, value);
}

/** Build EntityRef from form/schema option. Throws if id cannot be determined. */
export function toEntityRef(option: {
    id?: string | null;
    name?: string | null;
    label?: string | null;
    displayName?: string | null;
    value?: string | null;
    entityType?: string | null;
}): EntityRef {
    const id = (option.id ?? option.value ?? option.name ?? '').toString().trim();
    if (!id) throw new Error(ENTITY_REF_ID_REQUIRED);
    const displayName = (option.displayName ?? option.label ?? option.name ?? id).toString().trim();
    const entityType = option.entityType != null ? String(option.entityType).trim() : undefined;
    return { id, displayName: displayName || id, ...(entityType && { entityType }) };
}

/** Build EntityRef from API shape (Id, DisplayName, EntityType). Throws if Id is missing. */
export function entityRefFromApi(attr: {
    Id?: string | null;
    DisplayName?: string | null;
    EntityType?: string | null;
}): EntityRef {
    const id = (attr.Id ?? '').toString().trim();
    if (!id) throw new Error(ENTITY_REF_ID_REQUIRED + ' API must provide Id.');
    const displayName = (attr.DisplayName ?? '').toString().trim();
    const entityType = attr.EntityType != null ? String(attr.EntityType).trim() : undefined;
    return { id, displayName: displayName || id, ...(entityType && { entityType }) };
}
